// src/pages/Collection.jsx

//fix sort ui -> it expands and contracts according too the content
//on small screen -> filter button size and position to be adjusted

import React, { useContext, useEffect, useState, useCallback } from 'react';
import { ShopContext } from '../context/ShopContext';
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Button,
  Menu,
  Container,
  useTheme,
  useMediaQuery,
} from '@mui/material';

import ProductItem from '../components/ProductItem';

const Collection = () => {
  const { products, search, showSearch } = useContext(ShopContext);
  
  const [filterProducts, setFilterProducts] = useState([]);
  const [category, setCategory] = useState([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [sortOption, setSortOption] = useState('relevant');
  const [availableSubCategories, setAvailableSubCategories] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  
  // State for filter dropdown menu on mobile
  const [anchorEl, setAnchorEl] = useState(null);
  const openFilterMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const closeFilterMenu = () => {
    setAnchorEl(null);
  };


// Extract all unique categories and subcategories from products
useEffect(() => {
  if (!products?.length) return;

  const categories = new Set();
  const subCategories = new Set();

  const processSubCategory = (subCategoryValue) => {
    if (!subCategoryValue) return;
    
    if (Array.isArray(subCategoryValue)) {
      subCategoryValue.forEach(sc => sc && subCategories.add(sc.trim()));
    } else if (typeof subCategoryValue === 'string') {
      subCategoryValue.split(',').forEach(sc => {
        const trimmed = sc.trim();
        if (trimmed) subCategories.add(trimmed);
      });
    } else {
      const strValue = String(subCategoryValue).trim();
      if (strValue) subCategories.add(strValue);
    }
  };

  products.forEach(product => {
    // Process category
    if (product.category) {
      categories.add(product.category.trim());
    }
    
    // Process subcategory
    processSubCategory(product.subcategory || product.subCategory);
  });

  setAvailableCategories(prev => {
    const newCategories = Array.from(categories).sort();
    return JSON.stringify(prev) !== JSON.stringify(newCategories) 
      ? newCategories 
      : prev;
  });

  setAvailableSubCategories(prev => {
    const newSubCategories = Array.from(subCategories).sort();
    return JSON.stringify(prev) !== JSON.stringify(newSubCategories)
      ? newSubCategories
      : prev;
  });
}, [products]);

// Load saved filters on mount
useEffect(() => {
  const savedCategories = localStorage.getItem('selectedCategories');
  const savedSubCategories = localStorage.getItem('selectedSubCategories');
  
  // Only set if they exist and are not empty
  if (savedCategories) {
    const parsed = JSON.parse(savedCategories);
    if (parsed && parsed.length > 0) {
      setCategory(parsed);
    }
  }
  if (savedSubCategories) {
    const parsed = JSON.parse(savedSubCategories);
    if (parsed && parsed.length > 0) {
      setSelectedSubCategories(parsed);
    }
  }
}, []);

// Save filters when they change
useEffect(() => {
  const hasActiveFilters = category.length > 0 || selectedSubCategories.length > 0;
  
  if (hasActiveFilters) {
    // Only save if there are active filters
    if (category.length > 0) {
      localStorage.setItem('selectedCategories', JSON.stringify(category));
    } else {
      localStorage.removeItem('selectedCategories');
    }
    
    if (selectedSubCategories.length > 0) {
      localStorage.setItem('selectedSubCategories', JSON.stringify(selectedSubCategories));
    } else {
      localStorage.removeItem('selectedSubCategories');
    }
  } else {
    // Clear all if nothing is selected
    localStorage.removeItem('selectedCategories');
    localStorage.removeItem('selectedSubCategories');
  }
}, [category, selectedSubCategories]);


  // Toggle category checkbox
  const toggleCategory = (value) => {
    if (category.includes(value)) {
      setCategory(prev => prev.filter(item => item !== value));
      
    } else {
      setCategory(prev => [...prev, value]);

    }
  };

  // Toggle subcategory checkbox
  const toggleSubCategory = (value) => {
    const normalizedValue = value.trim();
    if (selectedSubCategories.includes(normalizedValue)) {
      setSelectedSubCategories(prev => prev.filter(item => item !== normalizedValue));
    } else {
      setSelectedSubCategories(prev => [...prev, normalizedValue]);
    }
  };

  // Apply filtering and sorting on products
  const applyFilter = useCallback(() => {
    let filtered = Array.isArray(products) ? [...products] : [];

    // Log search filter
    if(showSearch && search) {
      filtered = filtered.filter(item => {
        const matches = item.name.toLowerCase().includes(search.toLowerCase());
        return matches;
      });
    }

    // Log category filter
    if (category.length > 0) {
      filtered = filtered.filter(item => {
        const matches = category.includes(item.category);
        return matches;
      });
    }

    // Log subCategory filter
    if (selectedSubCategories.length > 0) {
      filtered = filtered.filter(item => {
        
        // Handle both subcategory and subCategory (case-insensitive) for backward compatibility
        const subCategoryValue = item.subcategory || item.subCategory;
        if (!subCategoryValue) {
          return false;
        }
        
        // Convert to array if it's a string, and handle potential comma-separated values
        let itemSubCategories = [];
        if (Array.isArray(subCategoryValue)) {
          itemSubCategories = subCategoryValue;
        } else if (typeof subCategoryValue === 'string') {
          // Handle comma-separated values and trim whitespace
          itemSubCategories = subCategoryValue.split(',').map(s => s.trim());
        } else {
          itemSubCategories = [String(subCategoryValue)];
        }        
        // Check for matches (case-insensitive comparison)
        const hasMatch = selectedSubCategories.some(selected => {
          const normalizedSelected = selected.trim().toLowerCase();
          return itemSubCategories.some(subCat => 
            String(subCat).trim().toLowerCase() === normalizedSelected
          );
        });
        
        return hasMatch;
      });
    }


    // Sorting
    if (sortOption === 'low-high') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortOption === 'high-low') {
      filtered.sort((a, b) => b.price - a.price);
    }
    
    setFilterProducts(filtered);
  }, [products, category, selectedSubCategories, sortOption, search, showSearch]);

  useEffect(() => {
    applyFilter();
  }, [applyFilter]);

  // Responsive handling: on small screens, show dropdown; on md and above, show sidebar.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Filter UI to be rendered inside the dropdown menu
  const filterMenuContent = (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        CATEGORY
      </Typography>
      <FormControl component="fieldset">
        {availableCategories.map((cat) => (
          <FormControlLabel 
            key={cat}
            control={
              <Checkbox 
                checked={category.includes(cat)} 
                onChange={() => toggleCategory(cat)} 
              />
            }
            label={cat.toUpperCase()}
          />
        ))}
      </FormControl>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          SUB CATEGORY
        </Typography>
        <FormControl component="fieldset">
          {availableSubCategories.map((subCat) => (
            <FormControlLabel 
              key={subCat}
              control={
                <Checkbox 
                  checked={selectedSubCategories.includes(subCat)} 
                  onChange={() => toggleSubCategory(subCat)} 
                />
              }
              label={subCat}
            />
          ))}
        </FormControl>
      </Box>
    </Box>
  );

  return (
    <>
      <Container
        sx={{ my: 4 }}
      >
        {/* Top Section: Title, Sorting, and Filter Dropdown (if mobile) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
            All Collections
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="sort-label">Sort By</InputLabel>
              <Select
                labelId="sort-label"
                value={sortOption}
                label="Sort By"
                onChange={(e) => setSortOption(e.target.value)}
              >
                <MenuItem value="relevant">Sort by Relevant</MenuItem>
                <MenuItem value="low-high">Sort by Low to High</MenuItem>
                <MenuItem value="high-low">Sort by High to Low</MenuItem>
              </Select>
            </FormControl>
            {isMobile && (
              <Button variant="outlined" onClick={openFilterMenu}>
                Filter
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Left Sidebar: Filters (only for non-mobile) */}
          {!isMobile && (
            <Box sx={{ width: { xs: '100%', md: '25%' }, mb: { xs: 4, md: 0 } }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                FILTER
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                CATEGORY
              </Typography>
              <FormControl component="fieldset">
                {availableCategories.map((cat) => (
                  <FormControlLabel 
                    key={cat}
                    control={
                      <Checkbox 
                        checked={category.includes(cat)} 
                        onChange={() => toggleCategory(cat)} 
                      />
                    }
                    label={cat}
                  />
                ))}
              </FormControl>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  SUB CATEGORY
                </Typography>
                <FormControl component="fieldset">
                  {availableSubCategories.map((subCat) => (
                    <FormControlLabel 
                      key={subCat}
                      control={
                        <Checkbox 
                          checked={selectedSubCategories.includes(subCat)} 
                          onChange={() => toggleSubCategory(subCat)} 
                        />
                      }
                      label={subCat}
                    />
                  ))}
                </FormControl>
              </Box>
            </Box>
          )}

          {/* Right Section: Products */}
          <Box sx={{ width: { xs: '100%', md: '75%' } }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'repeat(auto-fill, minmax(200px, 1fr))',
                  sm: 'repeat(auto-fill, minmax(220px, 1fr))',
                  md: 'repeat(3, 1fr)',
                },
                gap: 3,
              }}
            >
              {filterProducts.map((product) => (
                <ProductItem key={product.id} product={product} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* Mobile Filter Menu */}
        {isMobile && (
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={closeFilterMenu}
            PaperProps={{ sx: { p: 2 } }}
          >
            {filterMenuContent}
          </Menu>
        )}
      </Container>
    </>
  );
};

export default Collection;
